import type { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { TradingFeedResponse } from "../api/types";
import { TRADING_FEED_POLL_MS } from "./constants";
import { StatusDot } from "@/components/trading/StatusDot";

interface StatusBarProps {
  leftQuery: UseQueryResult<TradingFeedResponse>;
  rightQuery: UseQueryResult<TradingFeedResponse>;
}

type ConnState = { variant: "ok" | "warn" | "error"; label: string };

function connectionState(
  query: UseQueryResult<TradingFeedResponse>,
  side: "left" | "right",
  t: TFunction<"dashboard">,
): ConnState {
  const sideLabel = t(`statusBar.side.${side}`);
  if (query.isError)
    return { variant: "error", label: t("statusBar.pane.error", { side: sideLabel }) };
  if (query.isPending)
    return { variant: "warn", label: t("statusBar.pane.loading", { side: sideLabel }) };
  if (query.isFetching)
    return { variant: "warn", label: t("statusBar.pane.refreshing", { side: sideLabel }) };
  return { variant: "ok", label: t("statusBar.pane.live", { side: sideLabel }) };
}

function latestAsOf(t: TFunction<"dashboard">, left?: string, right?: string): string {
  const values = [left, right]
    .filter((value): value is string => Boolean(value))
    .map((value) => Date.parse(value))
    .filter((value) => Number.isFinite(value));

  if (values.length === 0) return t("statusBar.noTimestamp");
  return t("statusBar.asOf", {
    value: new Date(Math.max(...values)).toLocaleTimeString(),
  });
}

type Pipeline = { variant: "ok" | "warn"; label: string; tooltip?: string };

function pipelineHealth(
  leftQuery: UseQueryResult<TradingFeedResponse>,
  rightQuery: UseQueryResult<TradingFeedResponse>,
  t: TFunction<"dashboard">,
): Pipeline {
  if (leftQuery.isError || rightQuery.isError) {
    return {
      variant: "warn",
      label: t("statusBar.pipeline.degraded"),
      tooltip: t("statusBar.pipeline.degradedTooltip"),
    };
  }
  if (leftQuery.data?.material.is_stale || rightQuery.data?.material.is_stale) {
    return {
      variant: "warn",
      label: t("statusBar.pipeline.stale"),
      tooltip: t("statusBar.pipeline.staleTooltip"),
    };
  }
  return { variant: "ok", label: t("statusBar.pipeline.healthy") };
}

export function StatusBar({ leftQuery, rightQuery }: StatusBarProps) {
  const { t } = useTranslation("dashboard");
  const left = connectionState(leftQuery, "left", t);
  const right = connectionState(rightQuery, "right", t);
  const pipeline = pipelineHealth(leftQuery, rightQuery, t);

  return (
    <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-2 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-3">
        <StatusDot
          variant={left.variant}
          label={left.label}
          tooltip={left.variant === "error" ? t("statusBar.paneErrorTooltip") : undefined}
        />
        <StatusDot
          variant={right.variant}
          label={right.label}
          tooltip={right.variant === "error" ? t("statusBar.paneErrorTooltip") : undefined}
        />
        <span title={t("statusBar.latestTooltip")}>
          {latestAsOf(t, leftQuery.data?.as_of, rightQuery.data?.as_of)}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span title={t("statusBar.autoRefreshTooltip")}>
          {t("statusBar.autoRefresh", { seconds: TRADING_FEED_POLL_MS / 1_000 })}
        </span>
        <StatusDot variant={pipeline.variant} label={pipeline.label} tooltip={pipeline.tooltip} />
      </div>
    </footer>
  );
}
